import { useState, useEffect } from 'react';
import { getSupportedZipCodes } from '../data/market-data';
import { getSizeOptions } from '../utils/sqft-utils';
import './PropertyForm.css';

function PropertyForm({ onCalculate, onChange, isLoading }) {
  const zipCodes = getSupportedZipCodes();

  // Form state
  const [formData, setFormData] = useState({
    zipCode: '',
    bedrooms: '2',
    bathrooms: '1',
    sizeCategory: 'average',
    condition: 'good',
    utilities: 'tenant-all',  // Default: tenant pays all (most common in Philly)
    // Amenities
    parking: {
      garage: false,
      dedicated: false,
      street: false
    },
    hvac: 'none',
    laundry: 'none',
    outdoor: false,
    updated: false,
    petFriendly: false,
    dishwasher: false
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Size options update when bedrooms change
  const [sizeOptions, setSizeOptions] = useState([]);

  useEffect(() => {
    const options = getSizeOptions(parseInt(formData.bedrooms));
    setSizeOptions(options);
  }, [formData.bedrooms]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested parking checkboxes
    if (name.startsWith('parking.')) {
      const parkingType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        parking: {
          ...prev.parking,
          [parkingType]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error when user makes a change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Notify parent that form changed
    if (onChange) {
      onChange();
    }
  };

  // Handle blur for validation
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name);
  };

  // Validate a single field
  const validateField = (name) => {
    let error = null;

    if (name === 'zipCode' && !formData.zipCode) {
      error = 'Please select a neighborhood';
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    if (!formData.zipCode) {
      newErrors.zipCode = 'Please select a neighborhood';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({ zipCode: true });

    if (!validateForm()) {
      return;
    }

    // Pass form data to parent
    onCalculate(formData);
  };

  return (
    <form className="property-form" onSubmit={handleSubmit}>
      {/* Zip Code */}
      <div className={`form-group ${errors.zipCode && touched.zipCode ? 'has-error' : ''}`}>
        <label htmlFor="zipCode">Neighborhood / Zip Code</label>
        <select
          id="zipCode"
          name="zipCode"
          value={formData.zipCode}
          onChange={handleChange}
          onBlur={handleBlur}
          className={errors.zipCode && touched.zipCode ? 'error' : ''}
        >
          <option value="">Select a zip code...</option>
          {zipCodes.map(({ zipCode, neighborhood }) => (
            <option key={zipCode} value={zipCode}>
              {zipCode} - {neighborhood}
            </option>
          ))}
        </select>
        {errors.zipCode && touched.zipCode && (
          <span className="error-message">{errors.zipCode}</span>
        )}
        <span className="helper-text">
          Select a Philadelphia neighborhood for market data
        </span>
      </div>

      {/* Bedrooms */}
      <div className="form-group">
        <label htmlFor="bedrooms">Bedrooms</label>
        <select
          id="bedrooms"
          name="bedrooms"
          value={formData.bedrooms}
          onChange={handleChange}
        >
          <option value="0">Studio</option>
          <option value="1">1 Bedroom</option>
          <option value="2">2 Bedrooms</option>
          <option value="3">3 Bedrooms</option>
          <option value="4">4 Bedrooms</option>
        </select>
        <span className="helper-text">
          Each bedroom typically adds $250-400/mo
        </span>
      </div>

      {/* Bathrooms */}
      <div className="form-group">
        <label htmlFor="bathrooms">Bathrooms</label>
        <select
          id="bathrooms"
          name="bathrooms"
          value={formData.bathrooms}
          onChange={handleChange}
        >
          <option value="1">1 Bath</option>
          <option value="1.5">1.5 Baths</option>
          <option value="2">2 Baths</option>
          <option value="2.5">2.5 Baths</option>
          <option value="3">3 Baths</option>
        </select>
        <span className="helper-text">
          Extra half-bath adds ~$25-50/mo
        </span>
      </div>

      {/* Size Category */}
      <div className="form-group">
        <label htmlFor="sizeCategory">Size Category</label>
        <select
          id="sizeCategory"
          name="sizeCategory"
          value={formData.sizeCategory}
          onChange={handleChange}
        >
          {sizeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="helper-text">
          Estimated sqft based on bedroom count
        </span>
      </div>

      {/* Condition */}
      <div className="form-group">
        <label htmlFor="condition">Property Condition</label>
        <select
          id="condition"
          name="condition"
          value={formData.condition}
          onChange={handleChange}
        >
          <option value="excellent">Excellent - Newly renovated, premium finishes</option>
          <option value="good">Good - Well maintained, updated</option>
          <option value="fair">Fair - Functional, some wear</option>
          <option value="poor">Poor - Needs work, dated</option>
        </select>
        <span className="helper-text">
          Condition impacts rent by -15% to +10%
        </span>
      </div>

      {/* Utilities */}
      <div className="form-group">
        <label htmlFor="utilities">Utilities</label>
        <select
          id="utilities"
          name="utilities"
          value={formData.utilities}
          onChange={handleChange}
        >
          <option value="tenant-all">Tenant pays all utilities</option>
          <option value="tenant-electric-gas">Tenant pays electric & gas</option>
          <option value="tenant-electric">Tenant pays electric only</option>
          <option value="included">All utilities included in rent</option>
        </select>
        <span className="helper-text">
          HUD rates include utilities; price adjusts when tenant pays
        </span>
      </div>

      <hr className="section-divider" />
      <h3 className="section-title">Amenities</h3>

      {/* Parking - Multi-select Checkboxes */}
      <div className="form-group">
        <label>Parking</label>
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="parking.street"
              checked={formData.parking.street}
              onChange={handleChange}
            />
            <span>Street parking (+$35)</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="parking.dedicated"
              checked={formData.parking.dedicated}
              onChange={handleChange}
            />
            <span>Dedicated spot (+$85)</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="parking.garage"
              checked={formData.parking.garage}
              onChange={handleChange}
            />
            <span>Garage parking (+$125)</span>
          </label>
        </div>
        <span className="helper-text">
          Select all that apply - premiums are additive
        </span>
      </div>

      {/* HVAC - Dropdown */}
      <div className="form-group">
        <label htmlFor="hvac">Air Conditioning / HVAC</label>
        <select
          id="hvac"
          name="hvac"
          value={formData.hvac}
          onChange={handleChange}
        >
          <option value="none">No AC</option>
          <option value="window-optional">Window AC allowed (+$10)</option>
          <option value="window-provided">Window units provided (+$35)</option>
          <option value="split-window-allowed">Ductless mini-split + window AC allowed (+$45)</option>
          <option value="hybrid">Ductless mini-split + window units (+$50)</option>
          <option value="mini-split">Ductless mini-split (+$65)</option>
          <option value="central">Central air (+$75)</option>
        </select>
        <span className="helper-text">
          Common in Philly: hybrid systems with split units on main floor
        </span>
      </div>

      {/* Laundry - Dropdown */}
      <div className="form-group">
        <label htmlFor="laundry">Laundry</label>
        <select
          id="laundry"
          name="laundry"
          value={formData.laundry}
          onChange={handleChange}
        >
          <option value="none">No laundry on-site</option>
          <option value="in-building">In-building laundry (+$25)</option>
          <option value="in-unit">In-unit washer/dryer (+$100)</option>
        </select>
        <span className="helper-text">
          In-unit laundry is a top tenant priority in Philly
        </span>
      </div>

      {/* Amenity Checkboxes */}
      <div className="form-group">
        <label>Additional Features</label>
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="outdoor"
              checked={formData.outdoor}
              onChange={handleChange}
            />
            <span>Private outdoor space (+$75-100)</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="updated"
              checked={formData.updated}
              onChange={handleChange}
            />
            <span>Recently renovated kitchen/bath (+$150)</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="petFriendly"
              checked={formData.petFriendly}
              onChange={handleChange}
            />
            <span>Pet-friendly (+$50)</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="dishwasher"
              checked={formData.dishwasher}
              onChange={handleChange}
            />
            <span>Dishwasher (+$25)</span>
          </label>
        </div>
        <span className="helper-text">
          Select all features that apply to your property
        </span>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className={`calculate-btn ${isLoading ? 'loading' : ''}`}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="btn-spinner"></span>
            Calculating...
          </>
        ) : (
          'Calculate Rent'
        )}
      </button>
    </form>
  );
}

export default PropertyForm;
